export type NotificationLocale = "en-US" | "ja-JP" | "ko-KR";

/**
 * Shape of the notification UI strings, including the keys used for the
 * realtime in-app notification toast that appears when a `notification:new`
 * socket event arrives.
 */
export interface NotificationUiStrings {
  awardReceivedEntity: string;
  awardReceivedContent: string;
  quizPlayerJoined: string;
  quizWaitingPart1: string;
  quizWaitingPart2: string;
  quizAllCompleted: string;
  unknownNotificationMessage: string;
  /** Default banner title shown above the actor + preview line. */
  toastTitleSystem: string;
  /** Banner title when the toast is for a chat-related notification. */
  toastTitleChat: string;
  /** Banner title for vote/award/follow-style social notifications. */
  toastTitleSocial: string;
  /** Banner title for system announcements / warnings. */
  toastTitleAnnouncement: string;
  /** Generic short fallback line below the title when no preview is available. */
  toastDefaultPreview: string;
  /** Localized aria-label for the clickable toast container. */
  toastAriaLabelOpen: string;
  /** Aria-label for the close button inside the toast. */
  toastAriaLabelDismiss: string;
}

const STRINGS: Record<NotificationLocale, NotificationUiStrings> = {
  "en-US": {
    awardReceivedEntity: "awarded your {entityTitle}",
    awardReceivedContent: "awarded your content",
    quizPlayerJoined: "joined the challenge.",
    quizWaitingPart1: "is waiting for you to finish part 1.",
    quizWaitingPart2: "is waiting for you to finish part 2.",
    quizAllCompleted: "was the latest active player to finish. Results are ready.",
    unknownNotificationMessage: "sent you a notification.",
    toastTitleSystem: "New notification",
    toastTitleChat: "New chat update",
    toastTitleSocial: "New activity",
    toastTitleAnnouncement: "Announcement",
    toastDefaultPreview: "Tap to view your notifications",
    toastAriaLabelOpen: "Open notifications",
    toastAriaLabelDismiss: "Dismiss notification",
  },
  "ja-JP": {
    awardReceivedEntity: "あなたの「{entityTitle}」にアワードを送りました",
    awardReceivedContent: "あなたのコンテンツにアワードを送りました",
    quizPlayerJoined: "がチャレンジに参加しました。",
    quizWaitingPart1: "があなたのパート 1 完了を待っています。",
    quizWaitingPart2: "があなたのパート 2 完了を待っています。",
    quizAllCompleted: "が最後のアクティブ参加者として完了しました。結果を見ることができます。",
    unknownNotificationMessage: "通知を送りました。",
    toastTitleSystem: "新しい通知",
    toastTitleChat: "新しいチャットの更新",
    toastTitleSocial: "新しいアクティビティ",
    toastTitleAnnouncement: "お知らせ",
    toastDefaultPreview: "タップして通知を表示",
    toastAriaLabelOpen: "通知を開く",
    toastAriaLabelDismiss: "通知を閉じる",
  },
  "ko-KR": {
    awardReceivedEntity: "회원님의 {entityTitle}에 어워드를 보냈습니다",
    awardReceivedContent: "회원님의 콘텐츠에 어워드를 보냈습니다",
    quizPlayerJoined: "님이 챌린지에 참가했습니다.",
    quizWaitingPart1: "님이 당신이 1부를 마치기를 기다리고 있습니다.",
    quizWaitingPart2: "님이 당신이 2부를 마치기를 기다리고 있습니다.",
    quizAllCompleted: "님이 마지막 활성 참가자로 완료했습니다. 이제 결과를 볼 수 있습니다.",
    unknownNotificationMessage: "알림을 보냈습니다.",
    toastTitleSystem: "새 알림",
    toastTitleChat: "새 채팅 업데이트",
    toastTitleSocial: "새 활동",
    toastTitleAnnouncement: "공지사항",
    toastDefaultPreview: "탭하여 알림 보기",
    toastAriaLabelOpen: "알림 열기",
    toastAriaLabelDismiss: "알림 닫기",
  },
};

export function resolveNotificationLocale(): NotificationLocale {
  if (typeof navigator === "undefined" || !navigator.language) {
    return "en-US";
  }
  const lang = navigator.language.toLowerCase();
  if (lang.startsWith("ja")) {
    return "ja-JP";
  }
  if (lang.startsWith("ko")) {
    return "ko-KR";
  }
  return "en-US";
}

export function getNotificationUiStrings(locale?: NotificationLocale): NotificationUiStrings {
  const key = locale ?? resolveNotificationLocale();
  return STRINGS[key] ?? STRINGS["en-US"];
}
